/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { Component, inject, OnDestroy, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { GenericModalService } from 'src/app/shared/generic-modal/generic-modal.service';
import { LoadingPanelService } from 'src/app/shared/loading-panel/loading-panel.service';
import { ToastService, ToastType } from 'src/app/shared/toast/toast.service';
import { SystemService } from 'src/app/system/services/system.service';
import { MailSettingsTestMailComponent } from '../mail-settings-test-mail/mail-settings-test-mail.component';
import { MailSettings } from '../models/mail-settings';
import { ConfigurationService } from '../services/configuration.service';

import { PermissionRequiredDirective } from '../../shared/authentication/permission-required.directive';
import { Password, PasswordComponentValue } from '../../controls/password';
import { CurrentUserService } from 'src/app/user/services';

@Component({
  selector: 'app-mail-settings-pane',
  templateUrl: './mail-settings-pane.component.html',
  styleUrls: ['./mail-settings-pane.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FormsModule, ReactiveFormsModule, PermissionRequiredDirective, Password]
})
export class MailSettingsPaneComponent implements OnInit, OnDestroy {
  private formBuilder = inject(UntypedFormBuilder);
  private loadingPanelService = inject(LoadingPanelService);
  private modalService = inject(GenericModalService);
  private toastService = inject(ToastService);
  private systemService = inject(SystemService);
  private configurationService = inject(ConfigurationService);
  private currentUserService = inject(CurrentUserService);

  editMode = false;
  form: UntypedFormGroup;
  allowShowPassword = signal(false);
  useCredentialsChangesSubscription: Subscription = null;

  private mailSettings = {} as MailSettings;

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      host: ['', Validators.required],
      port: [0, Validators.required],
      enableSsl: [false],
      sender: ['', [Validators.required, Validators.email]],
      useCredentials: [false],
      username: ['', Validators.required],
      password: [null, Validators.required],
    });

    this.allowShowPassword.set(this.currentUserService.hasPermission('ShowPasswords'));

    // Disable username/password when not credentials are not required
    this.useCredentialsChangesSubscription = this.fcUseCredentails.valueChanges.subscribe(value => {
      if (value) {
        this.fcUsername.enable();
        this.fcPassword.enable();
      }
      else {
        this.fcUsername.disable();
        this.fcPassword.disable();
      }
    });

    void this.refresh();
  }

  ngOnDestroy(): void {
    this.useCredentialsChangesSubscription?.unsubscribe();
    this.useCredentialsChangesSubscription = null;
  }

  get fcHost(): UntypedFormControl {
    return this.form.get('host') as UntypedFormControl;
  }

  get fcPort(): UntypedFormControl {
    return this.form.get('port') as UntypedFormControl;
  }

  get fcEnableSsl(): UntypedFormControl {
    return this.form.get('enableSsl') as UntypedFormControl;
  }

  get fcSender(): UntypedFormControl {
    return this.form.get('sender') as UntypedFormControl;
  }

  get fcUseCredentails(): UntypedFormControl {
    return this.form.get('useCredentials') as UntypedFormControl;
  }

  get fcUsername(): UntypedFormControl {
    return this.form.get('username') as UntypedFormControl;
  }

  get fcPassword(): UntypedFormControl {
    return this.form.get('password') as UntypedFormControl;
  }

  async refresh(): Promise<void> {
    try {
      this.loadingPanelService.showLoadingPanelDirect('Loading...');
      this.mailSettings = await this.configurationService.getMailSettings();
      this.updateMailSettingsUI();
    }
    catch (err) {
      console.error(err);
      this.toastService.showToast('Failed to load data...', ToastType.error);
    }
    finally {
      this.loadingPanelService.hideLoadingPanel();
    }
  }

  editClicked(): void {
    this.editMode = true;
  }

  async saveClicked(): Promise<void> {
    try {
      this.editMode = false;

      this.mailSettings.SmtpHost = this.fcHost.value;
      this.mailSettings.SmtpPort = +this.fcPort.value;
      this.mailSettings.UseSsl = this.fcEnableSsl.value;
      this.mailSettings.SenderEmail = this.fcSender.value;
      this.mailSettings.UseNetworkCredentials = this.fcUseCredentails.value;
      this.mailSettings.Username = this.fcUsername.value;
      this.mailSettings.PasswordId = this.fcPassword.value.id;
      this.mailSettings.PasswordValue = this.fcPassword.value.value;
      this.mailSettings = await this.configurationService.saveMailSettings(this.mailSettings);

      this.toastService.showToast('Mail Settings saved...', ToastType.success);
    }
    catch {
      this.toastService.showToast('Failed to save mail settings ...', ToastType.error);
    }
  }

  cancelClicked(): void {
    this.editMode = false;
    this.form.reset();
    this.updateMailSettingsUI();
  }

  async sendTestMail(): Promise<void> {
    let recipient = '';
    const sender = this.fcSender.value;

    try {
      const testMailSettings = await this.modalService.openComponent(MailSettingsTestMailComponent);
      recipient = testMailSettings.to;
    }
    catch {
      // This just indicates that user cancelled the confirm dialog
      return;
    }

    try {
      let errorMessage = '';
      try {
        this.loadingPanelService.showLoadingPanelDirect('Sending...');
        errorMessage = await this.systemService.sendTestMail(sender, recipient);
      }
      finally {
        this.loadingPanelService.hideLoadingPanel();
      }

      if (errorMessage) {
        await this.modalService.messageDirect('Error', `The mail message could not be delivered. Error message: ${errorMessage}`);
      }
      else {
        await this.modalService.messageDirect('Success', `A mail message was successfully delivered to the Smtp server for recipient ${recipient}`);
      }
    }
    catch (err) {
      console.error(err);
      await this.modalService.messageDirect('Error', `The mail message could not be delivered. Error message: ${err?.error || 'Internal server error.'}`);
    }
  }

  updateMailSettingsUI(): void {
    this.form.patchValue({
      host: this.mailSettings.SmtpHost,
      port: this.mailSettings.SmtpPort,
      enableSsl: this.mailSettings.UseSsl,
      sender: this.mailSettings.SenderEmail,
      useCredentials: this.mailSettings.UseNetworkCredentials,
      username: this.mailSettings.Username,
      password: { id: this.mailSettings.PasswordId, value: this.mailSettings.PasswordValue } as PasswordComponentValue,
    });
  }
}
